/**
 * SDMS Result Validation — workflow action modals and helpers.
 *
 * Scoped to the result-validation kind of <AnalysisListPage>.  Owns the
 * Validate / Rollback / Preview / Open Package modals and the POST calls
 * to /api/v1/analysis/result-validation/{validate,rollback}.
 *
 * Phase 1: list-view parity with prototype.  The backend endpoints stub the
 * DB write and return success; the frontend optimistically updates the rows
 * (status pill → "SDMS Validated" / "Pending SDMS Validation", statusId →
 * DV-1-2 / DV-1-1, dateValidated + validatedByList stamped from the response).
 * Open Package detail view, SDMS spreadsheet popup and Project Notes popup
 * are Phase 2/3 features.
 */
import { useState, type ReactNode } from "react";
import { api } from "../../api/client";

// ── shared row shape (subset of AnalyticalBatchRow used by these actions) ───
export interface SdmsBatch {
  id: string; // SpreadSheetEntry_AnalyticalBatch.Oid
  batchId: string | null; // QCBatchID display
  statusText: string; // 'Pending Validation' | 'SDMS Validated' …
  statusId: string | null; // 'DV-1-1' | 'DV-1-2'
  test: string | null;
  method: string | null;
  templateID: number | null;
  validatedByList: string | null;
  validatedDateMax: string | null;
  // "Submitted By" (Batch Information header) — SpreadSheetEntry_AnalyticalBatch.
  // SamplesSubmittedBy, a distinct field from ValidatedByList (2026-07-17).
  submittedByList: string | null;
  /** "Date Submitted" — ALPACA MAX(sample SubmittedDate), not the batch CreatedDate. */
  submittedDateMax: string | null;
}

// Status the prototype calls "Pending" — the legacy SP returns "Pending Validation".
export const isPending = (r: SdmsBatch) => /pending/i.test(r.statusText) && !/validated/i.test(r.statusText);
export const isValidated = (r: SdmsBatch) => /validated/i.test(r.statusText);

// ── POST helpers ────────────────────────────────────────────────────────────
export interface ValidateResponse {
  success: boolean;
  count: number;
  validatedBy: string;
  validatedAt: string; // ISO
  /** PARTIAL VALIDATION (ALPACA parity): false ⇒ only some grid rows were
   *  validated and the batch stays "Pending SDMS Validation" — the caller must
   *  keep it in the queue and reload the grid rather than dropping it.  True /
   *  undefined ⇒ the batch is fully validated (Status 2→5). */
  fullyValidated?: boolean;
  /** Grid rows newly stamped by this call. */
  stampedSamples?: number;
  /** Grid rows still unvalidated in the batch after this call. */
  remainingSamples?: number;
  /** Per-batch skip reason keyed by batch Oid — populated when the server-side
   *  precondition gate refuses one or more of the requested batches (e.g. a
   *  batch still has unanswered check points). Empty when everything validated
   *  cleanly. Surfaced in the modal so the analyst sees the same message
   *  ALPACA XAF shows on its blue banner. */
  skipped?: Record<string, string>;
  note?: string;
}
export interface RollbackResponse {
  success: boolean;
  count: number;
  rolledBackBy: string;
  rolledBackAt: string;
  /** Per-batch skip reason keyed by batch Oid — populated when the server-side
   *  gate refuses one or more batches (e.g. report already generated).  The
   *  list-view surfaces this in the ALPACA-style red banner across the top of
   *  the page — same wording ALPACA XAF shows. */
  skipped?: Record<string, string>;
  note?: string;
}

// ── Review-Log write bus ────────────────────────────────────────────────────
// Any write that can add or change a DataPackageReviewLog row announces itself
// here, and `useReviewLog` re-fetches that batch. It lives at the WRITE helpers
// rather than at each button because there is more than one Validate in the app
// - the package detail's own button and the list-level confirm dialog - and the
// detail's Review Log went stale whenever the click came through a path that
// happened not to call its reload (client 2026-08-19: signed at 17:38:05, the
// grid still showed "Awaiting signature" until a manual F5 at 17:38:45; the API
// log proves no re-fetch was issued in between). Hanging the refresh off the
// POST itself means a new caller cannot forget it.
type ReviewLogWriteListener = (batchOid: string) => void;
const reviewLogWriteListeners = new Set<ReviewLogWriteListener>();

/** Subscribe to review-log-affecting writes. Returns the unsubscribe. */
export function onReviewLogWrite(fn: ReviewLogWriteListener): () => void {
  reviewLogWriteListeners.add(fn);
  return () => {
    reviewLogWriteListeners.delete(fn);
  };
}

function emitReviewLogWrite(...batchOids: (string | undefined)[]): void {
  for (const oid of batchOids) {
    if (!oid) continue;
    for (const fn of reviewLogWriteListeners) fn(oid);
  }
}

export async function postValidate(
  batchIds: string[],
  reason: string,
  sampleInfoIds?: string[],
): Promise<ValidateResponse> {
  const res = await api.post("/api/v1/analysis/result-validation/validate", {
    batchIds,
    reason: reason || null,
    // PARTIAL VALIDATION: the ticked Batch-Results rows
    // (SpreadSheetEntry_SampleInfo.Oid).  Omit/empty ⇒ whole-batch (list-view
    // bulk Validate).  ALPACA parity: DataReviewController.ValidateSamples.
    sampleInfoIds: sampleInfoIds && sampleInfoIds.length > 0 ? sampleInfoIds : null,
  });
  emitReviewLogWrite(...batchIds);
  return res.data as ValidateResponse;
}

export async function postRollback(batchIds: string[], reason: string): Promise<RollbackResponse> {
  const res = await api.post("/api/v1/analysis/result-validation/rollback", {
    batchIds,
    reason,
  });
  return res.data as RollbackResponse;
}

// ── Approve (SDMS Result Approval, Level-3) ─────────────────────────────────
export interface ApproveResponse {
  success: boolean;
  count: number;
  approvedBy: string;
  approvedAt: string; // ISO
  /** PARTIAL APPROVAL (ALPACA parity): false ⇒ only some grid rows were
   *  approved and the batch stays in the approval queue (NewStatus still
   *  "Validated"). True / undefined ⇒ fully approved (NewStatus → DV-6-4). */
  fullyApproved?: boolean;
  stampedSamples?: number;
  remainingSamples?: number;
  /** Per-batch skip reason keyed by batch Oid — populated when the server-side
   *  gate refuses one or more batches (already approved, not SDMS Validated). */
  skipped?: Record<string, string>;
  note?: string;
}

export async function postApprove(
  batchIds: string[],
  reason: string,
  sampleInfoIds?: string[],
): Promise<ApproveResponse> {
  const res = await api.post("/api/v1/analysis/sdms-result-approval/approve", {
    batchIds,
    reason: reason || null,
    sampleInfoIds: sampleInfoIds && sampleInfoIds.length > 0 ? sampleInfoIds : null,
  });
  emitReviewLogWrite(...batchIds);
  return res.data as ApproveResponse;
}

// ── SDMS package detail inline edits (ALPACA parity) ────────────────────────
// Legacy's batch-edit grid flushes pending Batch Results / Check Points /
// Review Log cell edits to the DB as part of Validate/Approve
// (dviSamples.InnerView.ObjectSpace.CommitChanges(), DataReviewController.cs
// :1971) — BEFORE the status flip. Call this immediately before postValidate /
// postApprove so an edited cell is never silently discarded.
export interface SdmsBatchResultEdit {
  id: string;
  rptLimit?: string | null;
  mcl?: string | null;
  spikeAmount?: string | null;
  dld?: string | null;
  qcStatus?: string | null;
  enteredBy?: string | null;
  enteredDate?: string | null;
}
export interface SdmsCheckPointEdit {
  id: string;
  qualifier?: string | null;
  comment?: string | null;
  decision?: string | null;
}
export interface SdmsReviewLogEdit {
  id: string;
  comment?: string | null;
  decisionStatus?: string | null;
  reviewScope?: string | null;
}
/** The reviewer's decision on the synthesized "SDMS Validation"/"SDMS Approval"
 *  Review Log row (no DB Oid yet). The API UPSERTS a real DataPackageReviewLog
 *  row for it — stamping Operator = the signer (so Role fills via the reader
 *  join), Date, DecisionStatus, ReviewScope and the comment — so it survives
 *  Validate. Legacy XAF creates this row at view-load (DataReviewController). */
export interface SdmsReviewEntry {
  processMode: string; // "SDMS Validation" | "SDMS Approval"
  comment?: string | null;
  decisionStatus?: string | null;
  reviewScope?: string | null;
}
export async function postSaveSdmsEdits(
  batchOid: string,
  edits: {
    batchResults?: SdmsBatchResultEdit[];
    checkPoints?: SdmsCheckPointEdit[];
    reviewLog?: SdmsReviewLogEdit[];
    sdmsReview?: SdmsReviewEntry | null;
  },
): Promise<{ success: boolean; count: number }> {
  const res = await api.post(`/api/v1/analysis/result-validation/${encodeURIComponent(batchOid)}/save-edits`, edits);
  emitReviewLogWrite(batchOid);
  return res.data as { success: boolean; count: number };
}

// ── shared modal scrim + frame ──────────────────────────────────────────────
function Modal({ children, onClose, width }: { children: ReactNode; onClose: () => void; width?: number }) {
  return (
    <div
      onClick={onClose}
      style={{
        position: "fixed",
        inset: 0,
        background: "rgba(15,18,28,.45)",
        zIndex: 9000,
        display: "flex",
        alignItems: "center",
        justifyContent: "center",
        padding: 20,
      }}
    >
      <div
        onClick={(e) => e.stopPropagation()}
        style={{
          background: "#fff",
          borderRadius: 12,
          boxShadow: "0 10px 34px rgba(20,24,40,.20)",
          width: Math.min(width ?? 560, 940),
          maxWidth: "94vw",
          overflow: "hidden",
        }}
      >
        {children}
      </div>
    </div>
  );
}

function ModalHead({ title, onClose }: { title: string; onClose: () => void }) {
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 10,
        padding: "14px 18px",
        borderBottom: "1px solid var(--ai-line)",
      }}
    >
      <span style={{ fontWeight: 700, fontSize: 15 }}>{title}</span>
      <button
        onClick={onClose}
        style={{
          marginLeft: "auto",
          border: "none",
          background: "transparent",
          fontSize: 20,
          color: "var(--ai-muted)",
          cursor: "pointer",
          lineHeight: 1,
        }}
      >
        ×
      </button>
    </div>
  );
}

function ModalFoot({ children }: { children: ReactNode }) {
  return (
    <div
      style={{
        display: "flex",
        gap: 8,
        justifyContent: "flex-end",
        padding: "13px 18px",
        borderTop: "1px solid var(--ai-line)",
        background: "var(--ai-header-bg)",
      }}
    >
      {children}
    </div>
  );
}

// ── Validate success popup ─────────────────────────────────────────────────
// ALPACA XAF-style post-save popup: centered dialog with a green tick,
// "Successfully Validated" title, single OK button.  Detail view calls
// postValidate() directly and only renders THIS on success — the
// intermediate "Validate N package(s)?" confirmation is skipped per
// user request (matches the ALPACA flow where the analyst clicks the
// toolbar Validate button and the save fires immediately).
export function ValidateSuccessModal({ count, onClose }: { count: number; onClose: () => void }) {
  return (
    <Modal onClose={onClose} width={440}>
      <div style={{ padding: "26px 22px 14px", textAlign: "center" }}>
        <div
          style={{
            width: 56,
            height: 56,
            borderRadius: "50%",
            background: "var(--ai-pill-ok-bg)",
            color: "var(--ai-green)",
            display: "inline-flex",
            alignItems: "center",
            justifyContent: "center",
            fontSize: 30,
            marginBottom: 12,
          }}
        >
          ✓
        </div>
        <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ai-ink)", marginBottom: 6 }}>
          Successfully Validated
        </div>
        <div style={{ fontSize: 13, color: "var(--ai-muted)", lineHeight: 1.45 }}>
          {count === 1 ? (
            <>
              The selected package has been marked as <b>SDMS Validated</b>.
            </>
          ) : (
            <>
              {count} packages have been marked as <b>SDMS Validated</b>.
            </>
          )}
          <br />
          Status moved <b>DV-1-1 → DV-1-2</b>.
        </div>
      </div>
      <ModalFoot>
        <button
          className="ca-btn ca-btn-solid"
          onClick={onClose}
          autoFocus
          style={{ background: "var(--ai-green)", borderColor: "var(--ai-green)", minWidth: 84 }}
        >
          OK
        </button>
      </ModalFoot>
    </Modal>
  );
}

// ── Validate modal (kept for list-view / bulk paths, unused in the detail
// view which now posts directly and shows only <ValidateSuccessModal>) ─────
// Two phases in one modal:
//   1. Confirmation — "Validate N package(s)?" + reason textarea + Validate button.
//   2. Success — after the POST succeeds, the same modal transforms into an
//      ALPACA-style success popup ("Validated successfully.") with an OK
//      button.  The parent's onConfirmed callback fires when the user clicks
//      OK (not when the POST resolves), so the analyst has to acknowledge the
//      result before the list refreshes — matches legacy XAF's post-save
//      confirmation dialog.
export function ValidateModal({
  targets,
  onClose,
  onConfirmed,
}: {
  targets: SdmsBatch[];
  onClose: () => void;
  onConfirmed: (resp: ValidateResponse, reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [successResp, setSuccessResp] = useState<ValidateResponse | null>(null);
  const n = targets.length;
  if (n === 0) return null;

  const submit = async () => {
    setBusy(true);
    setErr(null);
    try {
      const resp = await postValidate(
        targets.map((t) => t.id),
        reason.trim(),
      );
      // Server-side gate refused everything → keep the confirm view open and
      // show the same nag ALPACA XAF surfaces on its blue banner.
      const skipped = resp.skipped ?? {};
      const skipCount = Object.keys(skipped).length;
      if (resp.count === 0 && skipCount > 0) {
        setErr(Object.values(skipped).join(" · "));
        return;
      }
      // Success → transform to the ALPACA-parity success popup. onConfirmed
      // will fire when the analyst clicks OK.
      setSuccessResp(resp);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to validate");
    } finally {
      setBusy(false);
    }
  };

  // Phase 2 — success popup (ALPACA-parity: centered dialog with green tick,
  // "Successfully Validated." title, single OK button).
  if (successResp) {
    const ackAndClose = () => {
      onConfirmed(successResp, reason.trim());
      onClose();
    };
    return (
      <Modal onClose={ackAndClose} width={440}>
        <div style={{ padding: "26px 22px 14px", textAlign: "center" }}>
          <div
            style={{
              width: 56,
              height: 56,
              borderRadius: "50%",
              background: "var(--ai-pill-ok-bg)",
              color: "var(--ai-green)",
              display: "inline-flex",
              alignItems: "center",
              justifyContent: "center",
              fontSize: 30,
              marginBottom: 12,
            }}
          >
            ✓
          </div>
          <div style={{ fontSize: 16, fontWeight: 700, color: "var(--ai-ink)", marginBottom: 6 }}>
            Successfully Validated
          </div>
          <div style={{ fontSize: 13, color: "var(--ai-muted)", lineHeight: 1.45 }}>
            {n === 1 ? (
              <>
                The selected package has been marked as <b>SDMS Validated</b>.
              </>
            ) : (
              <>
                {successResp.count} package{successResp.count > 1 ? "s have" : " has"} been marked as{" "}
                <b>SDMS Validated</b>.
              </>
            )}
            <br />
            Status moved <b>DV-1-1 → DV-1-2</b>.
          </div>
        </div>
        <ModalFoot>
          <button
            className="ca-btn ca-btn-solid"
            onClick={ackAndClose}
            autoFocus
            style={{ background: "var(--ai-green)", borderColor: "var(--ai-green)", minWidth: 84 }}
          >
            OK
          </button>
        </ModalFoot>
      </Modal>
    );
  }

  // Phase 1 — confirmation.
  return (
    <Modal onClose={onClose}>
      <ModalHead title={`Validate ${n} package${n > 1 ? "s" : ""}?`} onClose={onClose} />
      <div style={{ padding: 18 }}>
        <p style={{ margin: "0 0 10px", fontSize: 13, color: "var(--ai-ink)" }}>
          Mark the selected package{n > 1 ? "s" : ""} as <b>SDMS Validated</b>. This stamps your analyst code and the
          validation time, and moves the status <b>DV-1-1 → DV-1-2</b>.
        </p>
        <label style={{ fontSize: 12, color: "var(--ai-muted)", display: "block", marginBottom: 4 }}>
          Reason / comment (optional)
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          placeholder="e.g. Reviewed raw runs & QC — within limits."
          disabled={busy}
          style={{
            width: "100%",
            minHeight: 64,
            padding: 8,
            fontSize: 13,
            fontFamily: "inherit",
            resize: "vertical",
            border: "1px solid var(--ai-line)",
            borderRadius: 7,
          }}
        />
        {err && <div style={{ marginTop: 8, color: "var(--ai-red)", fontSize: 12 }}>{err}</div>}
      </div>
      <ModalFoot>
        <button className="ca-btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
        <button
          className="ca-btn ca-btn-solid"
          onClick={submit}
          disabled={busy}
          style={{ background: "var(--ai-green)", borderColor: "var(--ai-green)" }}
        >
          {busy ? "Validating…" : "Validate"}
        </button>
      </ModalFoot>
    </Modal>
  );
}

// ── Rollback modal (reason REQUIRED) ────────────────────────────────────────
export function RollbackModal({
  targets,
  onClose,
  onConfirmed,
}: {
  targets: SdmsBatch[];
  onClose: () => void;
  onConfirmed: (resp: RollbackResponse, reason: string) => void;
}) {
  const [reason, setReason] = useState("");
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const n = targets.length;
  if (n === 0) return null;

  const submit = async () => {
    const r = reason.trim();
    if (!r) {
      setErr("A rollback reason is required.");
      return;
    }
    setBusy(true);
    setErr(null);
    try {
      const resp = await postRollback(
        targets.map((t) => t.id),
        r,
      );
      // The server-side gate refused every batch (typically "Rollback failed,
      // report already generated.").  Hand the response back to the parent so
      // it can surface the ALPACA-style red banner across the top of the list
      // view — same UX legacy XAF uses.  onConfirmed always fires; the parent
      // decides whether to show green (some succeeded) or red (all skipped).
      onConfirmed(resp, r);
    } catch (e) {
      setErr(e instanceof Error ? e.message : "Failed to roll back");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Modal onClose={onClose}>
      {/* ALPACA parity: the legacy "Raw Data Batch Rollback" popup
          (DataReviewController) — a title, a "RollBack Reason:" label + a tall
          reason box, and OK (orange) / Cancel.  Matched 1:1 per the LIMS dialog. */}
      <ModalHead title="Raw Data Batch Rollback" onClose={onClose} />
      <div style={{ padding: 18 }}>
        <label style={{ fontSize: 13, color: "var(--ai-ink)", display: "block", marginBottom: 6 }}>
          RollBack Reason:
        </label>
        <textarea
          value={reason}
          onChange={(e) => setReason(e.target.value)}
          disabled={busy}
          autoFocus
          style={{
            width: "100%",
            minHeight: 120,
            padding: 8,
            fontSize: 13,
            fontFamily: "inherit",
            resize: "vertical",
            border: "1px solid var(--ai-line)",
            borderRadius: 4,
          }}
        />
        {err && <div style={{ marginTop: 8, color: "var(--ai-red)", fontSize: 12 }}>{err}</div>}
      </div>
      <ModalFoot>
        <button
          className="ca-btn ca-btn-solid"
          onClick={submit}
          disabled={busy}
          style={{ background: "var(--brand-accent, #e8670a)", borderColor: "var(--brand-accent, #e8670a)" }}
        >
          {busy ? "Rolling back…" : "OK"}
        </button>
        <button className="ca-btn" onClick={onClose} disabled={busy}>
          Cancel
        </button>
      </ModalFoot>
    </Modal>
  );
}

// ── Preview modal (placeholder — full image preview is Phase 2) ─────────────
export function PreviewModal({ target, onClose }: { target: SdmsBatch; onClose: () => void }) {
  return (
    <Modal onClose={onClose} width={580}>
      <ModalHead title={`SDMS Result Image — ${target.batchId ?? "—"}`} onClose={onClose} />
      <div style={{ padding: 18 }}>
        <div
          style={{
            height: 240,
            borderRadius: 10,
            border: "1px dashed var(--ai-line)",
            background:
              "repeating-linear-gradient(45deg,var(--ai-readonly-bg),var(--ai-readonly-bg) 12px,var(--ai-line-soft) 12px,var(--ai-line-soft) 24px)",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            gap: 8,
            color: "var(--ai-muted)",
            fontSize: 13,
          }}
        >
          <i className="ti ti-photo" style={{ fontSize: 36, opacity: 0.55 }} />
          Scanned / instrument result image for {target.test ?? "—"} ({target.method ?? "—"})
          <div style={{ fontSize: 11, marginTop: 6, opacity: 0.8 }}>(Phase 2 — full image preview pending)</div>
        </div>
      </div>
      <ModalFoot>
        <button className="ca-btn" onClick={onClose}>
          Close
        </button>
      </ModalFoot>
    </Modal>
  );
}

// Open Package detail view moved to ./SdmsPackageDetail.tsx (Phase 2 — full
// tabbed Batch Results / Check Points / Review Log / Attachments overlay).

// Per-row Edit clicks open the SAME SdmsPackageDetail overlay as the
// DataPackage "Open Package" link (CLAUDE.md rule 12; legacy parity).  No
// separate inline-row-edit popup — the detail view IS the edit form.
